import type { RadioStation } from "./stations";

const NORMAL_POLL_MS = 4_000;
const BOUNDARY_POLL_MS = 750;
const BOUNDARY_APPROACH_SECONDS = 20;

export interface LiveTrack {
	album: string;
	artist: string;
	duration: number;
	elapsed: number;
	id: string;
	isLive: boolean;
	playedAt: number;
	title: string;
}

export interface LiveMetadataPayload {
	nowPlaying: LiveTrack;
	presentationDelay: number;
	previousPlaying: LiveTrack | null;
	serverTime: number;
	station: string;
}

export interface AudiblePresentation {
	elapsed: number;
	nextPollMs: number;
	track: LiveTrack;
	transitionInMs: number | null;
}

export interface LivePresentation extends AudiblePresentation {
	fresh: boolean;
	receivedAt: number;
	station: RadioStation;
}

export type PresentationMode = "continuous" | "station-entry";

function nonNegative(value: unknown): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function resolveAudiblePresentation(
	payload: LiveMetadataPayload,
	requestStartedAt: number,
	receivedAt: number,
	mode: PresentationMode = "continuous",
): AudiblePresentation | null {
	const current = payload.nowPlaying;
	const duration = nonNegative(current?.duration);
	if (!current || duration <= 0) return null;

	const serverTime = nonNegative(payload.serverTime);
	const playedAt = nonNegative(current.playedAt);
	const reportedElapsed = Number(current.elapsed);
	const sourceElapsed =
		Number.isFinite(reportedElapsed) && reportedElapsed >= 0
			? reportedElapsed
			: serverTime > 0 && playedAt > 0
				? nonNegative(serverTime - playedAt)
				: 0;
	const halfRoundTripSeconds = Math.min(
		1,
		Math.max(0, receivedAt - requestStartedAt) / 2_000,
	);
	const estimatedSourceElapsed = Math.min(
		duration,
		sourceElapsed + halfRoundTripSeconds,
	);
	const audibleCurrentElapsed =
		estimatedSourceElapsed - nonNegative(payload.presentationDelay);
	const secondsRemaining = Math.max(0, duration - estimatedSourceElapsed);
	const nextPollMs =
		secondsRemaining <= BOUNDARY_APPROACH_SECONDS
			? BOUNDARY_POLL_MS
			: NORMAL_POLL_MS;
	if (mode === "station-entry") {
		return {
			track: current,
			elapsed: Math.max(0, audibleCurrentElapsed),
			transitionInMs: null,
			nextPollMs,
		};
	}

	if (
		audibleCurrentElapsed < 0 &&
		nonNegative(payload.previousPlaying?.duration) > 0
	) {
		const previous = payload.previousPlaying;
		if (!previous) return null;
		return {
			track: previous,
			elapsed: Math.max(
				0,
				nonNegative(previous.duration) + audibleCurrentElapsed,
			),
			transitionInMs: Math.max(0, -audibleCurrentElapsed * 1_000),
			nextPollMs,
		};
	}

	return {
		track: current,
		elapsed: Math.max(0, audibleCurrentElapsed),
		transitionInMs: null,
		nextPollMs,
	};
}

export class MetadataSync {
	#abortController: AbortController | null = null;
	#activeStation: RadioStation | null = null;
	#onPresentation: (presentation: LivePresentation) => void;
	#pollTimer: number | null = null;
	#requestSequence = 0;
	#stationEntryActive = true;
	#stationEntryTrackIdentity: string | null = null;
	#stationGeneration = 0;
	#transitionTimer: number | null = null;

	constructor(onPresentation: (presentation: LivePresentation) => void) {
		this.#onPresentation = onPresentation;
	}

	start(station: RadioStation): void {
		this.stop();
		this.#activeStation = station;
		this.#stationEntryActive = true;
		this.#stationEntryTrackIdentity = null;
		void this.#refresh();
	}

	stop(): void {
		this.#activeStation = null;
		this.#stationEntryActive = false;
		this.#stationEntryTrackIdentity = null;
		this.#requestSequence += 1;
		this.#stationGeneration += 1;
		this.#abortController?.abort();
		this.#abortController = null;
		if (this.#pollTimer !== null) window.clearTimeout(this.#pollTimer);
		this.#clearTransitionTimer();
		this.#pollTimer = null;
	}

	async #refresh(): Promise<void> {
		const station = this.#activeStation;
		if (!station) return;
		const stationGeneration = this.#stationGeneration;
		const sequence = ++this.#requestSequence;
		const requestStartedAt = Date.now();
		this.#abortController = new AbortController();
		let nextPollMs = NORMAL_POLL_MS;

		try {
			const response = await fetch(station.metadata, {
				cache: "no-store",
				signal: this.#abortController.signal,
			});
			if (!response.ok)
				throw new Error(`Metadata request failed with ${response.status}`);
			const payload = (await response.json()) as LiveMetadataPayload;
			const receivedAt = Date.now();
			const currentTrackIdentity = trackIdentity(payload.nowPlaying);
			if (
				this.#stationEntryActive &&
				this.#stationEntryTrackIdentity === null
			) {
				this.#stationEntryTrackIdentity = currentTrackIdentity;
			}
			const isStationEntryTrack =
				this.#stationEntryActive &&
				currentTrackIdentity === this.#stationEntryTrackIdentity;
			if (this.#stationEntryActive && !isStationEntryTrack) {
				this.#stationEntryActive = false;
				this.#stationEntryTrackIdentity = null;
			}
			const resolved = resolveAudiblePresentation(
				payload,
				requestStartedAt,
				receivedAt,
				isStationEntryTrack ? "station-entry" : "continuous",
			);
			if (
				!resolved ||
				sequence !== this.#requestSequence ||
				station !== this.#activeStation
			)
				return;

			nextPollMs = resolved.nextPollMs;
			this.#clearTransitionTimer();
			this.#onPresentation({ ...resolved, fresh: true, receivedAt, station });
			if (resolved.transitionInMs !== null) {
				const transitionTrack = payload.nowPlaying;
				const transitionAt = receivedAt + resolved.transitionInMs;
				this.#transitionTimer = window.setTimeout(
					() => {
						this.#transitionTimer = null;
						if (
							stationGeneration !== this.#stationGeneration ||
							station !== this.#activeStation
						)
							return;
						const transitionedAt = Date.now();
						this.#onPresentation({
							track: transitionTrack,
							elapsed: Math.max(0, (transitionedAt - transitionAt) / 1_000),
							transitionInMs: null,
							nextPollMs,
							fresh: true,
							receivedAt: transitionedAt,
							station,
						});
					},
					Math.max(0, transitionAt - Date.now()),
				);
			}
		} catch (error) {
			if (!(error instanceof DOMException && error.name === "AbortError")) {
				console.warn(
					JSON.stringify({
						event: "desktop_metadata_unavailable",
						station: station.route,
					}),
				);
			}
		} finally {
			if (
				sequence === this.#requestSequence &&
				station === this.#activeStation
			) {
				this.#pollTimer = window.setTimeout(
					() => void this.#refresh(),
					nextPollMs,
				);
			}
		}
	}

	#clearTransitionTimer(): void {
		if (this.#transitionTimer !== null)
			window.clearTimeout(this.#transitionTimer);
		this.#transitionTimer = null;
	}
}

function trackIdentity(track: LiveTrack): string {
	const contentId = String(track.id || `${track.artist}:${track.title}`);
	return `${contentId}:${track.playedAt || 0}`;
}
